import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import AIAskSettingsModal from '../AIAskSettingsModal';
import CryptoTicker from '../CryptoTicker';
import ImportantDateModal from '../ImportantDateModal';
import ImportantDates from '../ImportantDates';
import Leaderboard from '../Leaderboard';
import LeaderboardModal from '../LeaderboardModal';
import Notes from '../Notes';
import PinnedMissions from '../PinnedMissions';
import TaskList from '../TaskList';
import QuickLinkModal from '../QuickLinkModal';
import QuickLinks from '../QuickLinks';
import TagClipboard from '../TagClipboard';
import TodoList from '../TodoList';
import { PlusIcon } from '../icons/PlusIcon';
import { auth, onAuthStateChanged } from '../../firebase/firebase';
import useModuleState, { resetModuleStateStorage } from '../../hooks/useModuleState';
import useFirestore from '../../hooks/useFirestore';
import useTimerModule from '../../hooks/useTimerModule';
import { AIAskSettings, askAI, readAIAskSettings, writeAIAskSettings } from '../../services/aiAsk';
import {
  AIAskMessage,
  AIAskState,
  DashboardModuleComponentProps,
  ImportantDate,
  QuickLink,
  Routine,
  Task,
  Todo,
} from '../../types';

interface DashboardModulesContextValue {
  tasks: Task[];
  isAdmin: boolean;
  onCreateTask: () => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (id: string) => void;
  onToggleTaskComplete: (id: string) => void;
}

const DashboardModulesContext = createContext<DashboardModulesContextValue | null>(null);

export const DashboardModulesProvider: React.FC<React.PropsWithChildren<DashboardModulesContextValue>> = ({
  children,
  ...value
}) => {
  const memoizedValue = useMemo(() => value, [value]);

  return (
    <DashboardModulesContext.Provider value={memoizedValue}>
      {children}
    </DashboardModulesContext.Provider>
  );
};

function useDashboardModules() {
  const context = useContext(DashboardModulesContext);
  if (!context) {
    throw new Error('useDashboardModules must be used inside DashboardModulesProvider');
  }
  return context;
}

function formatTimer(seconds: number): string {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  return [hours, minutes, remainingSeconds]
    .map((value) => value.toString().padStart(2, '0'))
    .join(':');
}

interface NotesModuleState {
  content: string;
  height: string;
}

const defaultNotesModuleState: NotesModuleState = {
  content: '',
  height: '12rem',
};

function isTodoArray(value: unknown): value is Todo[] {
  return Array.isArray(value);
}

function isRoutineArray(value: unknown): value is Routine[] {
  return Array.isArray(value);
}

function isQuickLinkArray(value: unknown): value is QuickLink[] {
  return Array.isArray(value);
}

function isImportantDateArray(value: unknown): value is ImportantDate[] {
  return Array.isArray(value);
}

function useQuickLinksModuleState() {
  return useModuleState<QuickLink[]>(
    'quicklinks',
    [],
    {
      storageKey: 'crypto-missions:quickLinks',
      migrate: (value) => (isQuickLinkArray(value) ? value : null),
    }
  );
}

function useLeaderboardModuleState() {
  return useModuleState<QuickLink[]>(
    'leaderboard',
    [],
    {
      storageKey: 'crypto-missions:leaderboards',
      migrate: (value) => (isQuickLinkArray(value) ? value : null),
    }
  );
}

function useTodosModuleState() {
  return useModuleState<Todo[]>(
    'todos',
    [],
    {
      storageKey: 'crypto-missions:todos',
      migrate: (value) => (isTodoArray(value) ? value : null),
    }
  );
}

function useNotesModuleState() {
  const legacyNotesHeight = useFirestore<string>('crypto-missions:notesHeight', defaultNotesModuleState.height);

  return useModuleState<NotesModuleState>(
    'notes',
    defaultNotesModuleState,
    {
      storageKey: 'crypto-missions:notes',
      migrate: (value, defaultData) => {
        if (typeof value === 'string') {
          return {
            content: value,
            height: legacyNotesHeight || defaultData.height,
          };
        }

        if (
          value &&
          typeof value === 'object' &&
          (
            ('content' in value && typeof (value as Partial<NotesModuleState>).content === 'string') ||
            ('notes' in value && typeof (value as { notes?: string }).notes === 'string')
          )
        ) {
          const candidate = value as Partial<NotesModuleState> & { notes?: string; notesHeight?: string };
          return {
            content: candidate.content ?? candidate.notes ?? defaultData.content,
            height: candidate.height ?? candidate.notesHeight ?? legacyNotesHeight ?? defaultData.height,
          };
        }

        return null;
      },
    }
  );
}

function useRoutinesModuleState() {
  return useModuleState<Routine[]>(
    'routines',
    [],
    {
      storageKey: 'crypto-missions:routines',
      migrate: (value) => (isRoutineArray(value) ? value : null),
    }
  );
}

function useImportantDatesModuleState() {
  return useModuleState<ImportantDate[]>(
    'important-dates',
    [],
    {
      storageKey: 'crypto-missions:importantDates',
      migrate: (value) => (isImportantDateArray(value) ? value : null),
    }
  );
}

export const MissionBoardModule: React.FC<DashboardModuleComponentProps> = () => {
  const { tasks, isAdmin, onCreateTask, onDeleteTask, onEditTask, onToggleTaskComplete } = useDashboardModules();
  const completedTasks = tasks.filter((task) => task.isCompleted).length;
  const totalTasks = tasks.length;

  return (
    <div className="space-y-4">
      <div>
        <p className="dashboard-shell-subtitle">{completedTasks} / {totalTasks} completed</p>
      </div>
      <div className="dashboard-progress-track h-2 w-full rounded-full">
        <div
          className="dashboard-progress-fill h-2 rounded-full transition-all duration-500"
          style={{ width: totalTasks > 0 ? `${(completedTasks / totalTasks) * 100}%` : '0%' }}
        />
      </div>
      {tasks.length === 0 ? (
        <div className="dashboard-surface-strong rounded-2xl p-6 text-center">
          <p className="dashboard-muted-body">등록된 미션이 아직 없습니다.</p>
          {isAdmin && (
            <button
              type="button"
              onClick={onCreateTask}
              className="dashboard-button-accent dashboard-button-text mt-4 inline-flex h-9 items-center rounded-xl px-4"
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              첫 미션 추가
            </button>
          )}
        </div>
      ) : (
        <TaskList
          tasks={tasks}
          onToggleComplete={onToggleTaskComplete}
          onDelete={onDeleteTask}
          onEdit={onEditTask}
        />
      )}
    </div>
  );
};

interface SimpleTimerModuleProps extends DashboardModuleComponentProps {
  title: string;
  description: string;
  mode: 'countdown' | 'countup';
  defaultDurationSec?: number;
  presets?: number[];
}

const SimpleTimerModule: React.FC<SimpleTimerModuleProps> = ({
  moduleId,
  title,
  description,
  mode,
  defaultDurationSec = 25 * 60,
  presets = [15, 25, 45],
}) => {
  const { isAdmin } = useDashboardModules();
  // Timer widgets persist only their own moduleId envelope and derive the live clock locally.
  const { state, displaySec, start, pause, reset, setDurationSec } = useTimerModule(moduleId, {
    mode,
    defaultDurationSec,
  });
  const durationPresets = presets.map((minutes) => ({
    minutes,
    durationSec: minutes * 60,
  }));

  return (
    <div className="space-y-4">
      <div className="dashboard-surface-raised rounded-2xl p-3 text-center">
        <p className="dashboard-badge-label dashboard-text-faint">{description}</p>
        <div className="dashboard-display-mono mt-3">
          {formatTimer(displaySec)}
        </div>
        {state.lastCompletedAt && (
          <p className="dashboard-meta-text mt-2">
            Last completed {new Date(state.lastCompletedAt).toLocaleString()}
          </p>
        )}
      </div>

      {durationPresets.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {durationPresets.map((preset) => (
            <button
              key={preset.durationSec}
              type="button"
              disabled={!isAdmin}
              onClick={() => setDurationSec(preset.durationSec)}
              className={`dashboard-button-text flex h-9 items-center justify-center rounded-xl border px-3 transition ${
                state.durationSec === preset.durationSec
                  ? 'dashboard-chip-active'
                  : 'dashboard-chip-neutral'
              } ${!isAdmin ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {preset.minutes}m
            </button>
          ))}
        </div>
      )}

      {mode === 'countup' && (
        <div className="dashboard-pill-neutral dashboard-button-text-compact flex h-9 items-center justify-center rounded-xl px-3 text-center uppercase tracking-[0.12em]">
          Target {Math.floor(state.durationSec / 60) || 0} min
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <button
          type="button"
          disabled={!isAdmin}
          onClick={state.isRunning ? pause : start}
          className={`dashboard-button-text flex h-9 items-center justify-center rounded-xl px-3 ${
            state.isRunning ? 'dashboard-button-warning' : 'dashboard-button-accent'
          } ${!isAdmin ? 'cursor-not-allowed opacity-60' : ''}`}
        >
          {state.isRunning ? 'Pause' : 'Start'}
        </button>
        <button
          type="button"
          disabled={!isAdmin}
          onClick={reset}
          className={`dashboard-button-neutral dashboard-button-text flex h-9 items-center justify-center rounded-xl px-3 ${
            !isAdmin ? 'cursor-not-allowed opacity-60' : ''
          }`}
        >
          Reset
        </button>
        <div className="dashboard-pill-neutral dashboard-button-text-compact flex h-9 items-center justify-center rounded-xl px-3 text-center uppercase tracking-[0.12em]">
          {title}
        </div>
      </div>
    </div>
  );
};

function trimMessages(messages: AIAskMessage[], maxMessages = 8): AIAskMessage[] {
  return messages.slice(-maxMessages);
}

const defaultAIAskState: AIAskState = {
  messages: [],
  inputDraft: '',
  lastModel: null,
  updatedAt: null,
};

const MODULE_RESET_DEFAULTS: Partial<Record<string, { storageKey: string; defaultData: unknown }>> = {
  'ai-ask': {
    storageKey: 'crypto-missions:modules:ai-ask',
    defaultData: defaultAIAskState,
  },
  quicklinks: {
    storageKey: 'crypto-missions:quickLinks',
    defaultData: [],
  },
  leaderboard: {
    storageKey: 'crypto-missions:leaderboards',
    defaultData: [],
  },
  todos: {
    storageKey: 'crypto-missions:todos',
    defaultData: [],
  },
  notes: {
    storageKey: 'crypto-missions:notes',
    defaultData: defaultNotesModuleState,
  },
  routines: {
    storageKey: 'crypto-missions:routines',
    defaultData: [],
  },
  'important-dates': {
    storageKey: 'crypto-missions:importantDates',
    defaultData: [],
  },
};

export async function resetDashboardModuleState(moduleId: string) {
  const target = MODULE_RESET_DEFAULTS[moduleId];
  if (!target) {
    return false;
  }

  await resetModuleStateStorage(target.storageKey, target.defaultData);
  return true;
}

function getAIAskErrorDetail(error: string | null, usePersonalKey: boolean): string | null {
  if (error === 'Enter Base URL, token, or API key') {
    return 'Save a Base URL, token, or API key in AI Ask settings.';
  }

  if (error === 'Check Base URL') {
    return 'Check the saved Base URL. Pasting /v1/models is okay.';
  }

  if (error === 'Check your token or API key') {
    return 'Check the saved token or API key.';
  }

  if (error === 'OPENAI_API_KEY missing') {
    return usePersonalKey
      ? 'Save your custom Base URL, token, or API key in AI Ask settings.'
      : 'Save custom API settings in AI Ask or configure the server key.';
  }

  if (error === 'Quota or rate limit reached') {
    return 'Check usage, billing, or endpoint rate limits.';
  }

  if (error === 'Try again soon') {
    return 'The AI endpoint may be temporarily unstable.';
  }

  return null;
}

function getAIAskBackendLabel(settings: AIAskSettings, lastModel: string | null): string {
  if (settings.usePersonalKey) {
    return lastModel ? `Custom API - ${lastModel}` : settings.baseUrl || 'Custom API';
  }

  return lastModel ? `Server OpenAI - ${lastModel}` : 'Server OpenAI - Firebase Functions';
}

export const AIAskModule: React.FC<DashboardModuleComponentProps> = ({ moduleId }) => {
  // Persistent modules own only their moduleId-scoped envelope through useModuleState.
  const { state, saveState } = useModuleState<AIAskState>(moduleId, defaultAIAskState);
  const [settingsUserId, setSettingsUserId] = useState<string | null>(
    () => auth.currentUser?.uid ?? null
  );
  const [draft, setDraft] = useState(state.inputDraft);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [settings, setSettings] = useState<AIAskSettings>(() => readAIAskSettings(auth.currentUser?.uid ?? null));

  useEffect(() => {
    setDraft(state.inputDraft);
  }, [state.inputDraft]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged((user) => {
      const nextUserId = user?.uid ?? null;
      setSettingsUserId(nextUserId);
      const stored = readAIAskSettings(nextUserId);
      // 저장된 커스텀 설정이 있을 때만 덮어씀 (auth 초기화 타이밍으로 인한 기본값 덮어쓰기 방지)
      if (stored.usePersonalKey || stored.baseUrl || stored.apiKey || stored.token) {
        setSettings(stored);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      if (draft === state.inputDraft) {
        return;
      }

      void saveState((current) => ({
        ...current,
        inputDraft: draft,
        updatedAt: Date.now(),
      }));
    }, 400);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [draft, saveState, state.inputDraft]);

  const handleSubmit = async () => {
    const prompt = draft.trim();
    if (!prompt || isLoading) return;

    const userMessage: AIAskMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: prompt,
      createdAt: Date.now(),
    };

    const nextMessages = trimMessages([...state.messages, userMessage]);
    setDraft('');
    setError(null);
    setIsLoading(true);

    await saveState((current) => ({
      ...current,
      messages: nextMessages,
      inputDraft: '',
      updatedAt: Date.now(),
    }));

    try {
      const result = await askAI(prompt, nextMessages, settings);
      const assistantMessage: AIAskMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: result.answer,
        createdAt: Date.now(),
      };

      await saveState((current) => ({
        ...current,
        messages: trimMessages([...nextMessages, assistantMessage]),
        inputDraft: '',
        lastModel: result.model,
        updatedAt: Date.now(),
      }));
    } catch (submissionError) {
      console.error('AI Ask failed:', submissionError);
      const message = submissionError instanceof Error ? submissionError.message : 'AI connection failed';
      setError(message);
      setDraft(prompt);
      await saveState((current) => ({
        ...current,
        messages: current.messages.filter((messageItem) => messageItem.id !== userMessage.id),
        inputDraft: prompt,
        updatedAt: Date.now(),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = async () => {
    setDraft('');
    setError(null);
    await saveState({
      ...defaultAIAskState,
      updatedAt: Date.now(),
    });
  };

  const handleSaveSettings = (nextSettings: AIAskSettings) => {
    const savedSettings = writeAIAskSettings(settingsUserId, nextSettings);
    setSettings(savedSettings);
    setError(null);
  };

  const errorDetail = getAIAskErrorDetail(error, settings.usePersonalKey);
  const backendLabel = getAIAskBackendLabel(settings, state.lastModel);

  return (
    <div className="space-y-4">
      <div className="dashboard-surface-chat max-h-72 space-y-3 overflow-y-auto rounded-2xl p-3">
        {state.messages.length === 0 ? (
          <div className="dashboard-surface-strong dashboard-muted-body rounded-2xl p-3">
            Ask about missions, planning, or quick research and keep a short recent history here.
          </div>
        ) : (
          state.messages.map((message) => (
            <div
              key={message.id}
              className={`dashboard-body-text rounded-2xl p-3 ${
                message.role === 'user'
                  ? 'dashboard-message-user ml-6'
                  : 'dashboard-message-assistant mr-6'
              }`}
            >
              <div className="dashboard-chat-label mb-1 opacity-70">
                {message.role === 'user' ? 'You' : 'AI'}
              </div>
              <p className="whitespace-pre-wrap break-words">{message.content}</p>
            </div>
          ))
        )}
        {isLoading && (
          <div className="dashboard-surface-raised dashboard-muted-body mr-6 rounded-2xl p-3">
            Thinking...
          </div>
        )}
      </div>

      {error && (
        <div className="dashboard-alert-error dashboard-body-text rounded-2xl p-3">
          <p>{error}</p>
          {errorDetail && (
            <p className="dashboard-meta-text mt-1 text-rose-500">
              {errorDetail}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              void handleSubmit();
            }
          }}
          placeholder="Ask the dashboard AI anything..."
          className="dashboard-input-surface dashboard-body-text min-h-28 w-full resize-y rounded-2xl p-3"
        />
        <div className="flex items-center justify-between gap-3">
          <div className="dashboard-meta-text">
            {backendLabel}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              className="dashboard-button-neutral dashboard-button-text flex h-9 items-center rounded-xl px-3"
            >
              API Settings
            </button>
            <button
              type="button"
              onClick={() => void handleClear()}
              className="dashboard-button-neutral dashboard-button-text flex h-9 items-center rounded-xl px-3"
            >
              Clear
            </button>
            <button
              type="button"
              disabled={isLoading || draft.trim().length === 0}
              onClick={() => void handleSubmit()}
              className={`dashboard-button-text flex h-9 items-center rounded-xl px-4 ${
                isLoading || draft.trim().length === 0
                  ? 'dashboard-button-accent-disabled cursor-not-allowed'
                  : 'dashboard-button-accent'
              }`}
            >
              {isLoading ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
      {isSettingsOpen && (
        <AIAskSettingsModal
          initialSettings={settings}
          onClose={() => setIsSettingsOpen(false)}
          onSave={handleSaveSettings}
          isSignedIn={settingsUserId !== null}
        />
      )}
    </div>
  );
};

export const QuickLinksModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  const { state: quickLinks, saveState } = useQuickLinksModuleState();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSaveQuickLink = async (link: Omit<QuickLink, 'id'>) => {
    if (!isAdmin || !link.title.trim() || !link.url.trim()) {
      return;
    }

    await saveState((current) => [
      ...current,
      { ...link, id: crypto.randomUUID() },
    ]);
  };

  const handleDeleteQuickLink = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => current.filter((link) => link.id !== id));
  };

  return (
    <>
      <QuickLinks
        links={quickLinks}
        onDelete={(id) => void handleDeleteQuickLink(id)}
        onOpenModal={() => setIsModalOpen(true)}
        isAdmin={isAdmin}
        onAdd={(title, url) => void handleSaveQuickLink({ title, url })}
      />
      {isModalOpen && (
        <QuickLinkModal
          onClose={() => setIsModalOpen(false)}
          onSave={(link) => void handleSaveQuickLink(link)}
        />
      )}
    </>
  );
};

export const MarketModule: React.FC<DashboardModuleComponentProps> = () => {
  return <CryptoTicker />;
};

export const LeaderboardModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  const { state: leaderboards, saveState } = useLeaderboardModuleState();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSaveLeaderboard = async (link: Omit<QuickLink, 'id'>) => {
    if (!isAdmin || !link.title.trim() || !link.url.trim()) {
      return;
    }

    await saveState((current) => [
      ...current,
      { ...link, id: crypto.randomUUID() },
    ]);
  };

  const handleDeleteLeaderboard = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => current.filter((link) => link.id !== id));
  };

  return (
    <>
      <Leaderboard
        links={leaderboards}
        onDelete={(id) => void handleDeleteLeaderboard(id)}
        onOpenModal={() => setIsModalOpen(true)}
        isAdmin={isAdmin}
        onAdd={(title, url) => void handleSaveLeaderboard({ title, url })}
      />
      {isModalOpen && (
        <LeaderboardModal
          onClose={() => setIsModalOpen(false)}
          onSave={(link) => void handleSaveLeaderboard(link)}
        />
      )}
    </>
  );
};

export const TodosModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  const { state: todos, saveState } = useTodosModuleState();

  const handleAddTodo = async (text: string) => {
    if (!isAdmin || text.trim() === '') {
      return;
    }

    await saveState((current) => [
      ...current,
      { id: crypto.randomUUID(), text, isCompleted: false },
    ]);
  };

  const handleToggleTodo = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) =>
      current.map((todo) =>
        todo.id === id ? { ...todo, isCompleted: !todo.isCompleted } : todo
      )
    );
  };

  const handleDeleteTodo = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => current.filter((todo) => todo.id !== id));
  };

  return (
    <TodoList
      todos={todos}
      onAddTodo={(text) => void handleAddTodo(text)}
      onToggleTodo={(id) => void handleToggleTodo(id)}
      onDeleteTodo={(id) => void handleDeleteTodo(id)}
      title="To-Do List"
      isAdmin={isAdmin}
    />
  );
};

export const NotesModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  const { state, saveState } = useNotesModuleState();

  const handleNotesChange = async (notes: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => ({
      ...current,
      content: notes,
    }));
  };

  const handleNotesHeightChange = async (notesHeight: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => ({
      ...current,
      height: notesHeight,
    }));
  };

  return (
    <Notes
      notes={state.content}
      onNotesChange={(notes) => void handleNotesChange(notes)}
      height={state.height}
      onHeightChange={(height) => void handleNotesHeightChange(height)}
      isAdmin={isAdmin}
    />
  );
};

export const RoutinesModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  const { state: routines, saveState } = useRoutinesModuleState();

  const handleAddRoutine = async (text: string) => {
    if (!isAdmin || text.trim() === '') {
      return;
    }

    await saveState((current) => [
      ...current,
      { id: crypto.randomUUID(), text, isCompleted: false },
    ]);
  };

  const handleToggleRoutine = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) =>
      current.map((routine) =>
        routine.id === id ? { ...routine, isCompleted: !routine.isCompleted } : routine
      )
    );
  };

  const handleDeleteRoutine = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => current.filter((routine) => routine.id !== id));
  };

  return (
    <TodoList
      todos={routines as Routine[]}
      onAddTodo={(text) => void handleAddRoutine(text)}
      onToggleTodo={(id) => void handleToggleRoutine(id)}
      onDeleteTodo={(id) => void handleDeleteRoutine(id)}
      title="Routine"
      isAdmin={isAdmin}
    />
  );
};

export const PomodoroModule: React.FC<DashboardModuleComponentProps> = ({ moduleId }) => {
  return (
    <SimpleTimerModule
      moduleId={moduleId}
      title="Pomodoro"
      description="Focus timer"
      mode="countdown"
      defaultDurationSec={25 * 60}
      presets={[15, 25, 45]}
    />
  );
};

export const WorkoutTimerModule: React.FC<DashboardModuleComponentProps> = ({ moduleId }) => {
  return (
    <SimpleTimerModule
      moduleId={moduleId}
      title="Workout Timer"
      description="Session timer"
      mode="countup"
      defaultDurationSec={20 * 60}
      presets={[10, 20, 30]}
    />
  );
};

export const ImportantDatesModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  const { state: importantDates, saveState } = useImportantDatesModuleState();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleSaveImportantDate = async (importantDate: Omit<ImportantDate, 'id'>) => {
    if (!isAdmin || !importantDate.text.trim() || !importantDate.date.trim()) {
      return;
    }

    await saveState((current) => [
      ...current,
      { ...importantDate, id: crypto.randomUUID() },
    ]);
  };

  const handleDeleteImportantDate = async (id: string) => {
    if (!isAdmin) {
      return;
    }

    await saveState((current) => current.filter((item) => item.id !== id));
  };

  return (
    <>
      <ImportantDates
        importantDates={importantDates}
        onDelete={(id) => void handleDeleteImportantDate(id)}
        onOpenModal={() => setIsModalOpen(true)}
        isAdmin={isAdmin}
        onAdd={(text, date) => void handleSaveImportantDate({ text, date })}
      />
      {isModalOpen && (
        <ImportantDateModal
          onClose={() => setIsModalOpen(false)}
          onSave={(date) => void handleSaveImportantDate(date)}
        />
      )}
    </>
  );
};

export const ClipboardModule: React.FC<DashboardModuleComponentProps> = () => {
  const { isAdmin } = useDashboardModules();
  return <TagClipboard isAdmin={isAdmin} onOpenModal={() => undefined} />;
};

export const PinnedMissionsModule: React.FC<DashboardModuleComponentProps> = () => {
  const { tasks } = useDashboardModules();
  const yapingMissions = useMemo(() => tasks.filter((task) =>
    task.tags.some((tag) => tag.toLowerCase() === 'yaping')
  ), [tasks]);

  if (yapingMissions.length === 0) {
    return <p className="dashboard-muted-body">No pinned yaping missions yet.</p>;
  }

  return <PinnedMissions tasks={yapingMissions} />;
};
